import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Lightning, Target, MagnifyingGlass, Eye, ChartLineUp, Lightbulb, Compass } from "@phosphor-icons/react";
import { SearchHeader } from "@/components/SearchHeader";
import { SidebarHistory } from "@/components/SidebarHistory";
import { SerpPanel } from "@/components/SerpPanel";
import { AiInsightsPanel } from "@/components/AiInsightsPanel";
import { MonitoringPanel } from "@/components/MonitoringPanel";
import { SearchTermsAuditor } from "@/components/SearchTermsAuditor";
import { LocationAuditor } from "@/components/LocationAuditor";
import { DemographicsAuditor } from "@/components/DemographicsAuditor";
import { AuctionInsightsAuditor } from "@/components/AuctionInsightsAuditor";
import { SurgicalTargeting } from "@/components/SurgicalTargeting";
import { KeywordPlanner } from "@/components/KeywordPlanner";
import { MarketResearch } from "@/components/MarketResearch";
import {
    runSearch,
    listHistory,
    getHistoryItem,
    deleteHistoryItem, errText,
} from "@/lib/api";

export default function ResearchTool() {
    const [view, setView] = useState("research"); // "research" | "monitor" | "performance"
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [pendingAudit, setPendingAudit] = useState(null); // { audit, subView }
    const [pendingDistiller, setPendingDistiller] = useState(null); // { audit, key }
    const [historyTick, setHistoryTick] = useState(0);
    // Ad-hoc scraped pages from the SERP "Scrape Keywords" button.
    // Keyed by record id so switching history entries doesn't leak state.
    const [adhocScrapes, setAdhocScrapes] = useState({});

    const refreshHistory = async () => {
        try {
            const items = await listHistory();
            setHistory(items || []);
        } catch (e) {
            // silent
        }
    };

    useEffect(() => {
        refreshHistory();
    }, []);

    const handleSubmit = async (payload) => {
        setLoading(true);
        setResult(null);
        setActiveId(null);
        const toastId = toast.loading("Scanning SERP + generating AI insights…");
        try {
            const data = await runSearch(payload);
            setResult(data);
            setActiveId(data.id);
            toast.success("Analysis complete", { id: toastId });
            refreshHistory();
        } catch (e) {
            const msg = errText(e, "Search failed");
            toast.error(msg, { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectHistory = async (id) => {
        // Called by SidebarHistory when the user clicks a SERP row.
        setView("research");
        setActiveId(id);
        try {
            const data = await getHistoryItem(id);
            setResult(data);
        } catch (e) {
            toast.error("Failed to load history item");
        }
    };

    const handleOpenAudit = (audit) => {
        // Called by SidebarHistory when the user clicks an Ads audit row.
        if (audit.kind === "ads_distiller") {
            // Route to Keyword Planner and hand the distiller run over so the
            // dialog re-opens with the persisted buckets pre-loaded.
            setPendingDistiller({ audit, key: `${audit.id}-${Date.now()}` });
            setActiveId(audit.id);
            setView("keyword_planner");
            return;
        }
        // Route to Performance tab + hand off the full audit to the right sub-view.
        let subView = "terms";
        if (audit.kind === "ads_geographic") subView = "locations";
        else if (audit.kind === "ads_auction_insights") subView = "auction";
        setPendingAudit({ audit, subView, key: `${audit.id}-${Date.now()}` });
        setActiveId(audit.id);
        setView("performance");
    };

    const handleDelete = async (id) => {
        try {
            await deleteHistoryItem(id);
            if (activeId === id) {
                setResult(null);
                setActiveId(null);
            }
            refreshHistory();
            toast.success("Deleted");
        } catch {
            toast.error("Failed to delete");
        }
    };

    const handleScrapedPage = (page) => {
        if (!result?.id || !page?.url) return;
        setAdhocScrapes((prev) => {
            const list = prev[result.id] || [];
            // Dedupe by URL — replace existing entry with fresh scrape.
            const filtered = list.filter((p) => p.url !== page.url);
            return { ...prev, [result.id]: [...filtered, page] };
        });
    };

    const currentAdhoc = result?.id ? adhocScrapes[result.id] || [] : [];

    return (
        <div className="flex h-screen w-screen overflow-hidden bg-white">
            <SidebarHistory
                items={history}
                activeId={activeId}
                onSelect={handleSelectHistory}
                onDelete={handleDelete}
                onOpenAudit={handleOpenAudit}
                refreshTick={historyTick}
            />

            <div className="flex flex-1 overflow-hidden flex-col">
                {/* Top tab bar */}
                <div className="border-b-2 border-ink flex items-stretch">
                    <button
                        data-testid="tab-research"
                        onClick={() => setView("research")}
                        className={`px-5 py-2.5 flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] font-bold border-r border-ink transition ${
                            view === "research" ? "bg-ink text-white" : "bg-white text-ink hover:bg-neutral-100"
                        }`}
                    >
                        <MagnifyingGlass size={13} weight="bold" />
                        Research
                    </button>
                    <button
                        data-testid="tab-monitor"
                        onClick={() => setView("monitor")}
                        className={`px-5 py-2.5 flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] font-bold border-r border-ink transition ${
                            view === "monitor" ? "bg-ink text-white" : "bg-white text-ink hover:bg-neutral-100"
                        }`}
                    >
                        <Eye size={13} weight="bold" />
                        Monitoring
                    </button>
                    <button
                        data-testid="tab-performance"
                        onClick={() => setView("performance")}
                        className={`px-5 py-2.5 flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] font-bold border-r border-ink transition ${
                            view === "performance" ? "bg-ink text-white" : "bg-white text-ink hover:bg-neutral-100"
                        }`}
                    >
                        <ChartLineUp size={13} weight="bold" />
                        Performance
                    </button>
                    <button
                        data-testid="tab-keyword-planner"
                        onClick={() => setView("keyword_planner")}
                        className={`px-5 py-2.5 flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] font-bold border-r border-ink transition ${
                            view === "keyword_planner" ? "bg-ink text-white" : "bg-white text-ink hover:bg-neutral-100"
                        }`}
                    >
                        <Lightbulb size={13} weight="bold" />
                        Keyword Planner
                    </button>
                    <button
                        data-testid="tab-prospecting"
                        onClick={() => setView("prospecting")}
                        className={`px-5 py-2.5 flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] font-bold border-r border-ink transition ${
                            view === "prospecting" ? "bg-ink text-white" : "bg-white text-ink hover:bg-neutral-100"
                        }`}
                    >
                        <Compass size={13} weight="bold" />
                        Prospecting
                    </button>
                </div>

                {view === "monitor" ? (
                    <MonitoringPanel />
                ) : view === "performance" ? (
                    <PerformanceView
                        pendingAudit={pendingAudit}
                        onAuditSaved={() => setHistoryTick((t) => t + 1)}
                    />
                ) : view === "keyword_planner" ? (
                    <KeywordPlanner
                        pendingDistiller={pendingDistiller}
                        onAuditSaved={() => setHistoryTick((t) => t + 1)}
                    />
                ) : view === "prospecting" ? (
                    <MarketResearch />
                ) : (
                    <div className="flex flex-1 overflow-hidden">
                        <main className="flex-1 flex flex-col overflow-hidden">
                            <SearchHeader
                                onSubmit={handleSubmit}
                                loading={loading}
                                initial={result}
                            />

                            <div className="flex-1 overflow-y-auto thin-scroll">
                                {!result && !loading && <EmptyState />}
                                {loading && <LoadingState />}
                                {result && (
                                    <div
                                        data-testid="results-grid"
                                        className="grid grid-cols-1 lg:grid-cols-12"
                                    >
                                        <div className="lg:col-span-7 lg:border-r lg:border-neutral-200">
                                            <SerpPanel
                                                ads={result.ads || []}
                                                organic={result.organic || []}
                                                related={result.related_searches || []}
                                                resolvedLocation={result.resolved_location}
                                                result={result}
                                                onScrapedPage={handleScrapedPage}
                                            />
                                        </div>
                                        <div className="lg:col-span-5">
                                            <AiInsightsPanel
                                                ai={result.ai || {}}
                                                result={result}
                                                adhocScrapes={currentAdhoc}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>
                )}
            </div>
        </div>
    );
}

const EmptyState = () => (
    <div
        data-testid="empty-state"
        className="h-full flex items-center justify-center px-6 py-16"
    >
        <div className="max-w-2xl w-full">
            <div className="mono-label">// getting started</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-ink mt-3">
                Reverse-engineer any
                <br />
                <span className="text-klein">local ads market.</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-700 mt-6 leading-relaxed max-w-xl">
                Enter a business type and a city. We pull live Google SERP data via DataForSEO,
                cross-reference every top competitor against Google&apos;s Ad Transparency Center,
                vision-read their actual ad screenshots, and hand Claude the raw intel to write
                your next winning campaign.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 mt-10 border-t border-l border-neutral-200">
                <FeatureTile
                    icon={<Crosshair size={20} weight="bold" />}
                    label="01 · scan"
                    title="Live SERP"
                    text="Ads, organic results, and related searches — pulled fresh, geo-targeted."
                />
                <FeatureTile
                    icon={<Lightning size={20} weight="bold" />}
                    label="02 · generate"
                    title="AI Copy"
                    text="5 headlines · 3 descriptions in Google Ads format, ready to paste."
                />
                <FeatureTile
                    icon={<Target size={20} weight="bold" />}
                    label="03 · exploit"
                    title="Gap Analysis"
                    text="Angles competitors are missing, keywords they're targeting."
                />
            </div>
        </div>
    </div>
);

const FeatureTile = ({ icon, label, title, text }) => (
    <div className="border-r border-b border-neutral-200 p-6 min-h-[160px]">
        <div className="flex items-center gap-2 mb-3">
            {icon}
            <span className="mono-label">{label}</span>
        </div>
        <h3 className="font-heading text-lg font-black tracking-tight text-ink">
            {title}
        </h3>
        <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{text}</p>
    </div>
);

const LoadingState = () => (
    <div
        data-testid="loading-state"
        className="h-full flex items-center justify-center p-8"
    >
        <div className="max-w-md text-center">
            <div className="mono-label mb-3">// scanning serp</div>
            <h2 className="font-display text-3xl font-black tracking-tighter text-ink">
                Interrogating Google.
            </h2>
            <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                Fetching live ads, parsing the top 5 organic listings, pulling related
                queries, then handing the payload to Claude for competitive analysis.
            </p>
            <div className="mt-8 flex justify-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                    <span
                        key={i}
                        className="inline-block w-2 h-8 bg-klein"
                        style={{
                            animation: `bar-slide 1s ease-in-out ${i * 0.12}s infinite alternate`,
                            opacity: 0.6,
                        }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const PerformanceView = ({ pendingAudit, onAuditSaved }) => {
    const [subView, setSubView] = useState("terms");

    // Auto-switch to the right sub-tab when opening from history.
    useEffect(() => {
        if (pendingAudit?.subView) setSubView(pendingAudit.subView);
    }, [pendingAudit]);

    const auditForTerms = pendingAudit?.subView === "terms" ? pendingAudit : null;
    const auditForGeo = pendingAudit?.subView === "locations" ? pendingAudit : null;
    const auditForAuction = pendingAudit?.subView === "auction" ? pendingAudit : null;

    return (
        <div className="flex flex-col flex-1 overflow-hidden">
            <div className="border-b border-neutral-300 bg-neutral-50 flex items-stretch">
                <button
                    data-testid="subtab-terms"
                    onClick={() => setSubView("terms")}
                    className={`px-4 py-2 flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] font-bold border-r border-neutral-300 transition ${
                        subView === "terms"
                            ? "bg-white text-ink border-b-2 border-b-klein"
                            : "text-neutral-500 hover:text-ink"
                    }`}
                >
                    Search Terms
                </button>
                <button
                    data-testid="subtab-locations"
                    onClick={() => setSubView("locations")}
                    className={`px-4 py-2 flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] font-bold border-r border-neutral-300 transition ${
                        subView === "locations"
                            ? "bg-white text-ink border-b-2 border-b-klein"
                            : "text-neutral-500 hover:text-ink"
                    }`}
                >
                    Locations
                </button>
                <button
                    data-testid="subtab-demographics"
                    onClick={() => setSubView("demographics")}
                    className={`px-4 py-2 flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] font-bold border-r border-neutral-300 transition ${
                        subView === "demographics"
                            ? "bg-white text-ink border-b-2 border-b-klein"
                            : "text-neutral-500 hover:text-ink"
                    }`}
                >
                    Demographics
                </button>
                <button
                    data-testid="subtab-auction"
                    onClick={() => setSubView("auction")}
                    className={`px-4 py-2 flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] font-bold border-r border-neutral-300 transition ${
                        subView === "auction"
                            ? "bg-white text-ink border-b-2 border-b-klein"
                            : "text-neutral-500 hover:text-ink"
                    }`}
                >
                    Auction Insights
                </button>
                <button
                    data-testid="subtab-surgical"
                    onClick={() => setSubView("surgical")}
                    className={`px-4 py-2 flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] font-bold border-r border-neutral-300 transition ${
                        subView === "surgical"
                            ? "bg-white text-ink border-b-2 border-b-klein"
                            : "text-neutral-500 hover:text-ink"
                    }`}
                >
                    Surgical Targeting
                </button>
            </div>
            {subView === "terms" ? (
                <SearchTermsAuditor preloadedAudit={auditForTerms} onAuditSaved={onAuditSaved} />
            ) : subView === "locations" ? (
                <LocationAuditor preloadedAudit={auditForGeo} onAuditSaved={onAuditSaved} />
            ) : subView === "auction" ? (
                <AuctionInsightsAuditor preloadedAudit={auditForAuction} onAuditSaved={onAuditSaved} />
            ) : subView === "surgical" ? (
                <SurgicalTargeting />
            ) : (
                <DemographicsAuditor />
            )}
        </div>
    );
};
